import React, { useEffect, useMemo, useState } from 'react';

function formatActuals(value) {
    if (value === '' || value == null) return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
}

export default function KPIReviewRow({
                                         kpi,
                                         review,
                                         ratings = [],
                                         onSave,
                                         counterText = ''
                                     }) {
    const ratingById = useMemo(() => {
        const m = new Map();
        ratings.forEach(r => m.set(String(r.id), r));
        return m;
    }, [ratings]);

    const [actuals, setActuals] = useState(formatActuals(review?.actuals ?? ''));
    const [employeeRatingId, setEmployeeRatingId] = useState(review?.employee_rating_id ?? '');
    const [saving, setSaving] = useState(false);

    const employeeRatingCode = useMemo(() => {
        if (!employeeRatingId) return '';
        const r = ratingById.get(String(employeeRatingId));
        return r?.code ?? '';
    }, [employeeRatingId, ratingById]);

    const managerRatingId = review?.manager_rating_id ?? '';
    const managerRatingCode = useMemo(() => {
        if (!managerRatingId) return '';
        const r = ratingById.get(String(managerRatingId));
        return r?.code ?? '';
    }, [managerRatingId, ratingById]);

    const onSubmit = async (nextActuals = actuals, nextEmployeeRatingId = employeeRatingId) => {
        if (!onSave || saving) return;
        const normalized = formatActuals(nextActuals);
        setActuals(normalized);

        const actualsValue = normalized === '' ? null : Number(normalized);
        const payload = {
            actuals: actualsValue,
            employee_rating_id: nextEmployeeRatingId || null,
            employee_description: nextEmployeeRatingId ? (ratingById.get(String(nextEmployeeRatingId))?.code ?? null) : null,
        };
        try {
            setSaving(true);
            await onSave({ reviewId: review?.id, kpiId: kpi.id, payload });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setActuals(formatActuals(review?.actuals ?? ''));
        setEmployeeRatingId(review?.employee_rating_id ?? '');
    }, [review?.actuals, review?.employee_rating_id]);

    const handleActualsBlur = (e) => {
        const val = e.target.value;
        setActuals(val);
        onSubmit(val, employeeRatingId);
    };

    const handleRatingChange = (e) => {
        const nextId = e.target.value ? Number(e.target.value) : '';
        setEmployeeRatingId(nextId);
        onSubmit(actuals, nextId);
    };

    return (
        <tr>
            <td className="rt-counter">{counterText}</td>
            <td className="rt-name">{kpi.name}</td>

            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.threshold ?? '—'}%` : (kpi.threshold ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>
            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.target ?? '—'}%` : (kpi.target ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>
            <td className="rt-num">
                {kpi.is_percentage ? `${kpi.stretch ?? '—'}%` : (kpi.stretch ?? '—')}
                {!kpi.is_percentage && kpi.unit ? ` ${kpi.unit}` : ''}
            </td>

            <td className="rt-num">{kpi.weight ?? '—'}</td>

            {/* Actuals (compact, custom) */}
            <td style={{ minWidth: 110 }}>
                <input
                    type="number"
                    className="rt-input"
                    value={actuals}
                    onChange={(e) => setActuals(e.target.value)}
                    onBlur={handleActualsBlur}
                    placeholder="Actuals"
                />
            </td>

            {/* Employee Rating (IDs) */}
            <td style={{ minWidth: 110 }}>
                <select
                    className="rt-select"
                    value={employeeRatingId || ''}
                    onChange={handleRatingChange}
                >
                    <option value="">--</option>
                    {ratings.map(r => (
                        <option key={r.id} value={r.id}>
                            {r.id}
                        </option>
                    ))}
                </select>
            </td>

            {/* Employee Description (auto code) */}
            <td style={{ minWidth: 140 }}>
                <input
                    className="rt-input"
                    value={employeeRatingCode}
                    readOnly
                    disabled
                    placeholder="Description"
                />
            </td>

            {/* Manager Rating (read-only ID) */}
            <td style={{ minWidth: 110 }}>
                <input
                    className="rt-input"
                    value={managerRatingId || ''}
                    readOnly
                    disabled
                    placeholder="—"
                />
            </td>

            {/* Manager Description (auto code) */}
            <td style={{ minWidth: 140 }}>
                <input
                    className="rt-input"
                    value={managerRatingCode}
                    readOnly
                    disabled
                    placeholder="Description"
                />
            </td>

            <td className="rt-actions" style={{ minWidth: 110 }}>
                {/* Auto-save on blur/change; no manual button needed */}
            </td>
        </tr>
    );
}
